<template>
    <div class="h-full border-r-2 border-gray-200 bg-gray-100 relative" :class="controlPanelClass">
    <base-tool-tip
        v-if="visibleToolTipPopover"
        :xPosition="xPosition"
        :yPosition="yPosition"
        class="z-50"
    >
        <template #title>
            {{ toolTipText }}
        </template>
    </base-tool-tip>
      <div class="h-8 px-2 sticky top-0 text-gray-800 tracking-wider font-medium flex justify-between items-center border-b-2 border-gray-300 bg-gray-150">
        <div class="flex justify-start items-center">
            <div
                v-if="displayPane('filters')"
                :style="filterFormat"
                class="hover:bg-gray-200 p-[9px] hover:cursor-pointer"
                @click="viewPanel('Filters')"
                ref="filtersBoundary"
                @mouseover="showToolTipPopover('filters', 'Filters')"
                @mouseleave="hideToolTipPopover"
            >
                <FilterSVG :fill="filterFill" />
            </div>
            <div
                v-if="displayPane('bulkOperations')"
                :style="bulkOperationFormat"
                class="hover:bg-gray-200 p-2 hover:cursor-pointer"
                @click="viewPanel('Bulk Operations')"
                ref="bulkOperationsBoundary"
                @mouseover="showToolTipPopover('bulkOperations', 'Bulk Operations')"
                @mouseleave="hideToolTipPopover"
            >
                <BulkOperationSVG :fill="bulkOperationsFill" />
            </div>
            <div
                v-if="displayPane('summary')"
                :style="summaryFormat"
                class="hover:bg-gray-200 p-2 hover:cursor-pointer"
                @click="viewPanel('CRM Summary')"
                ref="summaryBoundary"
                @mouseover="showToolTipPopover('summary', 'CRM Summary')"
                @mouseleave="hideToolTipPopover"
            >
                <CardTextSVG :fill="summaryFill" icon-class="h-4" />
            </div>
        </div>
        <!-- <div class="w-full flex justify-center items-center">
            {{ visiblePanel }}
        </div> -->
        <div ref="refreshBoundary" class="w-[100px] h-full flex justify-end items-center">
            <PaginationLoader
                class="hover:bg-gray-200 p-[5px]"
                @refetch-results="refetchResults"
                :isLoading="isLoading"
                @mouseover="showToolTipPopover('refresh', 'Refresh Results')"
                @mouseleave="hideToolTipPopover"
           />
        </div>
      </div>
      <div class="h-full">
        <FilterList
            v-show="visiblePanel=='Filters'"
            :filters="filters"
            :reorderFilters="reorderFilters"
            :width="width"
            :default-tab="defaultFilterTab"
            @refetch-results="refetchResults"
            @change-active-filters="changeActiveFilters"
            @change-filters="changeFilters"
            @save-filter-order="saveFilterOrder"
            @save-default-filters="saveDefaultFilters"
            @set-filter-options="setFilterOptions"
            @toggle-pinned="togglePinnedFilter"
        />
        <BulkOperationList
            v-show="visiblePanel=='Bulk Operations'"
            :width="width"
            :selectedResults="selectedResults"
            :selectedResultFields="selectedResultFields"
            :bulkOperations="bulkOperations"
            :bulkOperationResponse="bulkOperationResponse"
            :reorderBulkOperations="reorderBulkOperations"
            :fetch-filter="fetchFilter"
            @refetch-results="refetchResults"
            @clear-selected-results="clearSelectedResults"
            @save-bulk-operation-order="saveBulkOperationOrder"
            @perform-bulk-action="performBulkAction"
            @toggle-pinned="togglePinnedBulkOperation"
            @handle-bulk-operation-input-change="handleBulkOperationInputChange"
            @set-bulk-operation-options="setBulkOperationOptions"
        />
        <CRMStatusSummaryList
            v-show="visiblePanel=='CRM Summary'"
            :summary-actions="summaryActions"
            :width="width"
            :fetch-filter="fetchFilter"
            :result-count="resultCount"
            :is-loading="isLoading"
            :button-reset-id="summaryButtonResetId"
            @perform-summary-action="performSummaryAction"
        />
      </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, reactive, watch } from 'vue'
import FilterList from './FilterList.vue'
import BulkOperationList from './BulkOperationList.vue';
import CRMStatusSummaryList from './CRMStatusSummaryList.vue';
import CardTextSVG from '../svg-components/CardTextSVG.vue';
import FilterSVG from "@/components/svg-components/FilterSVG.vue";
import BulkOperationSVG from '@/components/svg-components/BulkOperationSVG.vue';
import PaginationLoader from "./PaginationLoader.vue";

const props = defineProps([
    'filters', 
    'bulkOperations', 
    'summaryActions',
    'selectedResults',
    'selectedResultFields',
    'isLoading',
    'width',
    'bulkOperationResponse',
    'reorderBulkOperations',
    'reorderFilters',
    'fetchFilter',
    'resultCount',
    'panes',
    'summaryButtonResetId',
    'activeFilters',
    'defaultFilterTab'
])

const emit = defineEmits([
    'change-filters',
    'save-filter-order',
    'save-default-filters',
    'change-active-filters',
    'set-filter-options',
    'hide-active-filters',
    'clear-select-all',
    'refetch-results',
    'clear-selected-results',
    'perform-bulk-action',
    'save-bulk-operation-order',
    'perform-summary-action',
    'toggle-pinned-filter',
    'toggle-pinned-bulk-operation',
    'change-active-filters',
    'handle-bulk-operation-input-change',
    'set-bulk-operation-options',
])

const xPosition = ref(0);
const yPosition = ref(0);
const visibleToolTipPopover = ref(false);
const toolTipText = ref(null);
const filtersBoundary = ref(null);
const bulkOperationsBoundary = ref(null);
const summaryBoundary = ref(null);
const refreshBoundary = ref(null);

const controlPanelClass = computed(() => {
    if(props.width) {
        return props.width
    } else {
        return 'w-72'
    }
})

const filters = reactive(props.filters);

const filterFormat = computed(() => {
    if(visiblePanel.value == 'Filters') {
        if(props.activeFilters.length > 0) {
            return 'background-color: #ffd230;'
        } else {
            return 'background-color: #d1d5dc'
        }
  
        // return 'background-color: #d1d5dc;'
    } 
    return ''
})

const bulkOperationFormat = computed(() => {
    if(visiblePanel.value == 'Bulk Operations') {
        return 'background-color: #d1d5dc'
        // return 'background-color: #d1d5dc;'
    }
    return ''
})

const summaryFormat = computed(() => {
    if(visiblePanel.value == 'CRM Summary') {
        return 'background-color: #d1d5dc'
        // return 'background-color: #d1d5dc;'
    }
    return ''
})


const visiblePanel = ref(
    props.panes.includes('filters') ? 'Filters' : props.panes.includes('bulkOperations') ? 'Bulk Operations' : props.panes.includes('summary') ? 'CRM Summary' : null
)

const filterFill = computed(() => {
    if(props.activeFilters.length > 0 && visiblePanel.value != 'Filters') {
        return '#ffb900'
    }
    return '#374151'
})

const bulkOperationsFill = ref('#374151');
const summaryFill = ref('#374151'); 

function viewPanel(panelType) {
    visiblePanel.value = panelType
}

function displayPane(paneName) {
    return props.panes.includes(paneName)
}

function refetchResults() {
    emit('refetch-results')
}

function clearSelectedResults() {
    emit('clear-selected-results')
}

function changeActiveFilters(activeFiltersValue) {
    // console.log('changeActiveFilters')
    // console.log(activeFiltersValue)
    emit('change-active-filters', activeFiltersValue)
}

function changeFilters(filters) {
    emit('change-filters', filters)
}

function setFilterOptions(filterName) {
    // console.log('ControlPanel: setFilterOptions', filterName)
    emit('set-filter-options', filterName)
}

function saveFilterOrder() {
    emit('save-filter-order')
}

function saveDefaultFilters(resolve) {
    emit('save-default-filters', resolve)
}

function saveBulkOperationOrder() {
    emit('save-bulk-operation-order')
}

function showToolTipPopover(elementName, text) {
      if (elementName == 'filters') {
        xPosition.value = filtersBoundary.value.getBoundingClientRect().left
        yPosition.value = filtersBoundary.value.getBoundingClientRect().bottom + 5
      } else if(elementName == 'bulkOperations') {
        xPosition.value = bulkOperationsBoundary.value.getBoundingClientRect().left
        yPosition.value = bulkOperationsBoundary.value.getBoundingClientRect().bottom + 5
      } else if(elementName == 'summary') {
        xPosition.value = summaryBoundary.value.getBoundingClientRect().left
        yPosition.value = summaryBoundary.value.getBoundingClientRect().bottom + 5
      } else if(elementName == 'refresh') {
        xPosition.value = refreshBoundary.value.getBoundingClientRect().left
        yPosition.value = refreshBoundary.value.getBoundingClientRect().bottom + 5
      }
      visibleToolTipPopover.value = true;
      toolTipText.value = text;
  }

function hideToolTipPopover() {
visibleToolTipPopover.value = false;
toolTipText.value = null;
}

function togglePinnedFilter(key) {
    emit('toggle-pinned-filter', key)
}

function togglePinnedBulkOperation(key) {
    emit('toggle-pinned-bulk-operation', key)
}

function performBulkAction(bulkActionData) {
    // console.log('ControlPanel: performBulkAction')
    emit('perform-bulk-action', bulkActionData)
}

function handleBulkOperationInputChange(bulkAction, inputKey, inputValue) {
    emit('handle-bulk-operation-input-change', bulkAction, inputKey, inputValue)
}

function setBulkOperationOptions(bulkOperationKey) {
    emit('set-bulk-operation-options', bulkOperationKey)
}

function performSummaryAction(summaryActionData) {
    // console.log("ControlPanel: performSummaryAction", summaryActionData)
    emit('perform-summary-action', summaryActionData)
}

</script>